import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { cn } from 'shared/utils'
import { Icon, Overlay } from '@wmgtech/legato'

import { SearchProps } from './Search.props'

import { debounce } from 'shared/utils/debounce'
import SearchListExtended from '../SearchListExtended/SearchListExtended'
import SearchList from '../SearchList/SearchList'

import './Search.scss'
import { TSearchResults } from 'shared/types'

import { ConditionRender } from 'components'
import { IconName } from '@fortawesome/fontawesome-common-types'

function Search({
  searchItems,
  onChooseItem,
  clearSearchResult,
  searchResult,
  className,
  isExtended = false,
  withFollow = false,
  isShort = false,
  placeHolder = 'Search',
  align = 'center',
  inputId = 'search-input',
  dataTest,
  children,
  disabled = false,
  searchBoxIconName = 'file-magnifying-glass',
  loading
}: SearchProps) {

  const [isMenuOpen, toggleMenu] = useState(false)
  const [value, setValue] = useState('')
  const [isArchivedShow, toggleArchivedShow] = useState(false)

  const inputRef = useRef(null)

  const { pathname } = useLocation()

  const { campaigns = [], products = [] } = searchResult
  const isResultsResponseExist = Object.keys(searchResult).length > 0
  const isSearchResultLength = campaigns.length + products.length

  const searchItemsHandler = useMemo(() => debounce((value: any) => searchItems(value), 400), [searchItems])

  const setValueHandler = useCallback(
    ({ target: { value } }) => {
      setValue(value)

      if (isShort) return searchItemsHandler(value)

      if (value.replace(/\s/g, '').length >= 3) return searchItemsHandler(value)
    },
    [isShort, searchItemsHandler]
  )

  const onItemChooseHandler = useCallback(
    (item) => () => {
      if (item && onChooseItem) {
        onChooseItem(item)
      }
      toggleMenu(false)
    },
    [onChooseItem]
  )
  const icon = loading ? 'spinner' : searchBoxIconName

  const toggleArchivedHandler = useCallback(() => toggleArchivedShow(!isArchivedShow), [isArchivedShow])

  useEffect(() => {
    setValue('')

    clearSearchResult && clearSearchResult()
  }, [pathname, clearSearchResult])

  useEffect(() => {
    if (loading) {
      toggleMenu(false)
    } else {
      if (value === '') {
        toggleMenu(false)
        clearSearchResult && clearSearchResult()
      } else {
        toggleMenu(true)
      }
    }

  }, [loading, isSearchResultLength, value, clearSearchResult])

  return (
    <div className={cn('search', className)}>
      <Overlay
        open={isMenuOpen && isResultsResponseExist}
        onOpenChange={(open) => {
          if (open) {
            if (isSearchResultLength > 0) toggleMenu(true)
          } else {
            toggleMenu(false)
          }
        }}
        className={'search-results-container'}
        placement="bottom"
        showFang={false}
        hooks={{
          click: {
            enabled: true,
            keyboardHandlers: false
          }
        }}
        trigger={
          <div className="search-box">
            <label className="search-box__label" htmlFor={inputId}>
              {children}
              <input
                ref={inputRef}
                id={inputId}
                className="search-box__field"
                onChange={setValueHandler}
                value={value}
                placeholder={placeHolder}
                autoComplete="off"
                data-test={`${dataTest}-input`}
                disabled={disabled}
              />
              <Icon className={cn('search-box__icon',{
                'fa-spin': loading,
              })}  name={icon as IconName} />
            </label>
          </div>
        }
      >
        <div className={cn('search-menu', `search-menu--${align}`)}>
          {[campaigns, products].map((items: TSearchResults[], index: number) => (
            <ConditionRender
              key={items[0]?.id || index}
              condition={isExtended}
              component={
                <SearchListExtended
                  titles={['campaigns', 'products']}
                  listItems={items}
                  toggleArchivedShow={toggleArchivedHandler}
                  isArchivedShow={isArchivedShow}
                  itemIndex={index}
                  dataTest={dataTest}
                />
              }
              secondComponent={
                <SearchList listItems={items} onItemChooseHandler={onItemChooseHandler} withFollow={withFollow} />
              }
            />
          ))}
        </div>
      </Overlay>
    </div>
  )
}

export default Search
